import { EventEmitter } from "../../services";
import { Base, BaseConfiguration } from "../../services";
import { ComponentEvent } from "../event";

export type ComponentConfiguration = BaseConfiguration & {
    html?: string | Element,
    class?: string,
    content_element?: string | HTMLElement,
};

type CustomElement = {}

type OffsetRect = {
    top: number,
    right: number,
    bottom: number,
    left: number,
    width: number,
    height: number
}

export class Component extends Base
{
    protected configuration: ComponentConfiguration;

    protected eventEmitter: EventEmitter;

    protected element: HTMLElement = null;

    protected content: HTMLElement = null;

    constructor(...configurations: ComponentConfiguration[])
    {
        const defaults: ComponentConfiguration = {
            html: "",
            class: "",
            content_element: null,
        };

        super(defaults, ...configurations);

        this.eventEmitter = new EventEmitter();

        this.build();
    }

    public build(): this
    {
        let html: Element | string = this.configuration.html ? this.configuration.html : this.render();

        if (html instanceof Element)
        {
            this.element = <HTMLElement>html;
        }
        else
        {
            let template: HTMLElement = document.createElement("div");

            template.innerHTML = html.trim();
            this.element = <HTMLElement>template.firstChild;
        }

        if (this.configuration.class)
        {
            this.addClass(this.configuration.class);
        }

        this.initContent();

        return this;
    }

    public render(): string
    {
        return "<div></div>";
    }

    public getElement(): HTMLElement
    {
        return this.element;
    }

    public getContent(): HTMLElement
    {
        return this.content;
    }

    public getElementOffsetRect(): OffsetRect
    {
        return this.getOffsetRect(this.element);
    }

    public getContentOffsetRect(): OffsetRect
    {
        return this.getOffsetRect(this.content);
    }

    public addClass(className: string[] | string): this
    {
        if (Array.isArray(className))
            className = className.join(" ");

        if (className.trim() === "")
            return this;

        const classes: string[] = className.split(" ");

        for (let i = 0; i < classes.length; i++)
        {
            if (classes[i])
            {
                this.element.classList.add(classes[i]);
            }
        }

        return this;
    }

    public removeClass(className: string[] | string): this
    {
        if (Array.isArray(className))
            className = className.join(" ");

        if (className.trim() === "")
            return this;

        const classes: string[] = className.split(" ");

        for (let i = 0; i < classes.length; i++)
        {
            if (classes[i])
            {
                this.element.classList.remove(classes[i]);
            }
        }

        return this;
    }

    public hasClass(className: string): boolean
    {
        return this.element.classList.contains(className);
    }

    public querySelector(selectors: string): any
    {
        return this.element.querySelector(selectors);
    }

    public querySelectorAll(selectors: string): NodeListOf<HTMLElement>
    {
        return this.element.querySelectorAll(selectors);
    }

    public setAttribute(name: string, value: string): this
    {
        this.element.setAttribute(name, value);

        return this;
    }

    public getAttribute(name: string): string | null
    {
        return this.element.getAttribute(name);
    }

    public removeAttribute(name: string): this
    {
        this.element.removeAttribute(name);

        return this;
    }

    public appendChild(element: Element | Component): this
    {
        if (element instanceof Component)
            this.content.appendChild(element.getElement());
        else
            this.content.appendChild(element);

        return this;
    }

    public appendTo(element: Element | Component): this
    {
        if (element instanceof Component)
            element.getContent().appendChild(this.element);
        else
        {
            element = this.getHtmlElement(element);
            element.appendChild(this.element);
        }

        return this;
    }

    public prependTo(element: Element | Component | string): this
    {
        if (element instanceof Component)
            element.getContent().prepend(this.element);
        else
        {
            element = this.getHtmlElement(element);
            element.prepend(this.element);
        }

        return this;
    }

    public insertBefore(element: Element | Component | string): this
    {
        if (element instanceof Component)
            element.getElement().parentNode.insertBefore(this.element, element.getElement());
        else
        {
            element = this.getHtmlElement(element);
            element.parentNode.insertBefore(this.element, element);
        }

        return this;
    }

    public insertAfter(element: Element | Component | string): this
    {
        if (element instanceof Component)
            element.getElement().parentNode.insertBefore(this.element, element.getElement().nextSibling);
        else
        {
            element = this.getHtmlElement(element);
            element.parentNode.insertBefore(this.element, element.nextSibling);
        }

        return this;
    }

    public contains(element: Element | Component, inclusive: boolean = true): boolean
    {
        let node: Node = element instanceof Component ? element.getElement() : <Node>element;

        if (inclusive && element === this.element)
            return true;

        while (node.parentNode)
        {
            if (node.parentNode === this.element)
                return true;

            node = node.parentNode;
        }

        return false;
    }

    public empty(): this
    {
        while (this.content.lastChild)
            this.content.removeChild(this.content.lastChild);

        return this;
    }

    public remove(): this
    {
        this.element.remove();

        return this;
    }

    public show(options: object = {}): this
    {
        this.element.style.display = "";

        return this;
    }

    public hide(options: object = {}): this
    {
        this.element.style.display = "none";

        return this;
    }

    public addEventListener(type: string, listener: Function, useCapture: boolean = false): this
    {
        this.element.addEventListener(type, <EventListenerOrEventListenerObject>listener, useCapture);

        return this;
    }

    public removeEventListener(type: string, listener: Function, useCapture: boolean = false): this
    {
        this.element.removeEventListener(type, <EventListenerOrEventListenerObject>listener, useCapture);

        return this;
    }

    public dispatchEvent(type: string, detail: object = {}): this
    {
        let defaultDetail: object =
            {
                target: this,
            };

        let event: Event = new CustomEvent(type, { bubbles: true, detail: Object.assign(defaultDetail, detail) });
        this.element.dispatchEvent(event);

        return this;
    }

    public on(eventName: string, fn: Function, useCapture: boolean = false): this
    {
        this.addEventListener(eventName, fn);

        return this;
    }

    public off(eventName: string, fn: Function): this
    {
        this.removeEventListener(eventName, fn);

        return this;
    }

    public trigger(type: string, detail: object = {}): this
    {
        this.dispatchEvent(type, detail);

        return this;
    }

    public isDisabled(): boolean
    {
        return this.hasClass("u-disabled");
    }

    public disable(): this
    {
        this.addClass("u-disabled");
        this.trigger(ComponentEvent.DISABLE);

        return this;
    }

    public enable(): this
    {
        this.removeClass("u-disabled");
        this.removeClass("u-loading");
        this.trigger(ComponentEvent.ENABLE);

        return this;
    }

    public loading(loading: boolean = true): this
    {
        /*
        this.addClass("u-loading");
        this.disable();
         */

        if (loading === false)
        {
            this.removeClass("u-loading");
            this.removeClass("u-disabled");
        }
        else
        {
            this.addClass("u-loading");
            this.addClass("u-disabled");
        }

        return this;
    }

    protected getOffsetRect(element: HTMLElement): OffsetRect
    {
        // Return zeros for disconnected and hidden (display: none) elements
        if (!element.getClientRects().length)
            return { top: 0, right: 0, bottom: 0, left: 0, width: 0, height: 0 };

        const rect: DOMRect = element.getBoundingClientRect();
        const win: Window = element.ownerDocument.defaultView;
        const scrollLeft: number = win.scrollX; //pageXOffset;
        const scrollTop: number = win.scrollY;

        return {
            top: rect.top + scrollTop,
            right: rect.right + scrollLeft,
            bottom: rect.bottom + scrollTop,
            left: rect.left + scrollLeft,
            width: rect.width,
            height: rect.height,
        };
    }

    protected initContent(): this
    {
        let content_element: string | HTMLElement = this.configuration.content_element;

        if (content_element)
        {
            this.content = this.getHtmlElement(content_element);
        }

        if (!this.content)
            this.content = this.element;

        return this;
    }

    protected getHtmlElement(element: Element | string): HTMLElement
    {
        return typeof element === "string" ? this.querySelector(element) : element;
    }

    /*
        __handleComponentRelease(event)
        {
            this.trigger(ComponentEvent.RELEASE, { event: event });
        }
     */
}
